import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { 
  BarChart3, TrendingUp, TrendingDown, AlertTriangle, Package, Clock, 
  Building2, Trash2, Undo2, Calendar, ShoppingCart, Globe, Zap, 
  CalendarDays, RefreshCw, Store, ArrowUpRight, ArrowDownRight, Minus,
  ChevronRight, Filter, Download, XCircle
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BRANCHES = {
  "all": "All Branches",
  "QC": "Quezon City",
  "PQ": "Parañaque",
  "CV": "Cavite",
  "PM": "Pampanga",
  "VZ": "Valenzuela",
  "OTG-A": "Orion On The Go - A",
  "OTG-B": "Orion On The Go - B",
  "OTG-C": "Orion On The Go - C"
};

const BRANCH_ONLY = {
  "QC": "Quezon City",
  "PQ": "Parañaque",
  "CV": "Cavite",
  "PM": "Pampanga",
  "VZ": "Valenzuela"
};

// Helper function to calculate smart restock data
const calculateSmartRestockData = (item) => {
  const stock = item.current_stock || 0;
  const avgDaily = item.avg_daily || 0;
  const daysLeft = item.days_left;
  
  let projectedOutDate = null;
  if (typeof daysLeft === 'number' && daysLeft > 0 && daysLeft < 9999) {
    const outDate = new Date();
    outDate.setDate(outDate.getDate() + Math.floor(daysLeft));
    projectedOutDate = outDate.toLocaleDateString('en-PH', { month: 'short', day: 'numeric' });
  }
  
  const targetDays = 30;
  const targetStock = avgDaily * targetDays;
  const suggestedRestock = Math.max(0, Math.ceil(targetStock - stock));
  
  let priorityScore = 0;
  let priorityLabel = 'LOW';
  let priorityColor = 'green';
  
  if (stock === 0) {
    priorityScore = 100;
    priorityLabel = 'CRITICAL';
    priorityColor = 'red';
  } else if (daysLeft === '∞' || avgDaily === 0) {
    priorityScore = 10;
    priorityLabel = 'LOW';
    priorityColor = 'slate';
  } else if (typeof daysLeft === 'number') {
    if (daysLeft <= 7) {
      priorityScore = 90 - daysLeft;
      priorityLabel = 'HIGH';
      priorityColor = 'red';
    } else if (daysLeft <= 14) {
      priorityScore = 70 - (daysLeft - 7) * 3;
      priorityLabel = 'MEDIUM';
      priorityColor = 'amber';
    } else if (daysLeft <= 30) {
      priorityScore = 50 - (daysLeft - 14);
      priorityLabel = 'WATCH';
      priorityColor = 'yellow';
    } else {
      priorityScore = Math.max(5, 30 - (daysLeft - 30) / 10);
      priorityLabel = 'LOW';
      priorityColor = 'green';
    }
  }
  
  let velocity = 'NONE';
  if (avgDaily >= 5) velocity = 'FAST';
  else if (avgDaily >= 2) velocity = 'GOOD';
  else if (avgDaily >= 0.5) velocity = 'SLOW';
  else if (avgDaily > 0) velocity = 'VERY SLOW';
  
  return {
    ...item,
    projectedOutDate,
    suggestedRestock,
    priorityScore,
    priorityLabel,
    priorityColor,
    velocity
  };
};

export default function AnalyticsDashboard() {
  const { user } = useAuth();
  const isAdmin = user?.can_access_all_branches || user?.is_admin || user?.role === "admin" || false;
  
  // Main filters
  const [selectedBranch, setSelectedBranch] = useState("all");
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [branchOverview, setBranchOverview] = useState(null);
  const [topSelling, setTopSelling] = useState(null);
  const [avgDailyData, setAvgDailyData] = useState(null);
  const [fastSlowData, setFastSlowData] = useState(null);
  const [daysToLastData, setDaysToLastData] = useState(null);
  const [monthlyComparison, setMonthlyComparison] = useState(null);
  
  // Daily Sales Report
  const [dateRangeStart, setDateRangeStart] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [dateRangeEnd, setDateRangeEnd] = useState(() => new Date().toISOString().slice(0, 10));
  const [salesByDate, setSalesByDate] = useState(null);
  const [loadingSalesByDate, setLoadingSalesByDate] = useState(false);
  const [reportBranch, setReportBranch] = useState("all");
  
  // Days to Last
  const [daysToLastBranch, setDaysToLastBranch] = useState("all");
  const [loadingDaysToLast, setLoadingDaysToLast] = useState(false);
  
  // Monthly Comparison
  const [monthA, setMonthA] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return d.toISOString().slice(0, 7);
  });
  const [monthB, setMonthB] = useState(() => new Date().toISOString().slice(0, 7));
  
  // Clear data
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [dataSummary, setDataSummary] = useState(null);
  const [clearing, setClearing] = useState(false);
  const [clearBranch, setClearBranch] = useState("QC");
  const [clearOptions, setClearOptions] = useState({
    walk_in_sales: true,
    online_sales: true,
    warranty_transactions: true,
    inventory_records: true,
    movement_logs: true
  });
  const [dateRangeType, setDateRangeType] = useState("custom");
  const [customDateFrom, setCustomDateFrom] = useState("2020-01-01");
  const [customDateTo, setCustomDateTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [pendingDeletions, setPendingDeletions] = useState([]);
  
  // Quick delete per branch
  const [showQuickDelete, setShowQuickDelete] = useState(false);
  const [quickDeleteBranch, setQuickDeleteBranch] = useState(null);
  const [quickDeleting, setQuickDeleting] = useState(false);
  const [quickDateFrom, setQuickDateFrom] = useState("2020-01-01");
  const [quickDateTo, setQuickDateTo] = useState(() => {
    // Default to end of last month (keep current month)
    const d = new Date();
    d.setDate(0); // Last day of previous month
    return d.toISOString().slice(0, 10);
  });
  const [deleteConfirmText, setDeleteConfirmText] = useState(""); // Must type "DELETE" to confirm

  // Date range helper
  const getDateRange = () => {
    const today = new Date().toISOString().slice(0, 10);
    const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
    switch (dateRangeType) {
      case "today": return { from: today, to: today };
      case "yesterday": return { from: yesterday, to: yesterday };
      case "custom": return { from: customDateFrom, to: customDateTo };
      default: return { from: today, to: today };
    }
  };

  // Fetch all main data
  const fetchAllData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("auth_token");
      const headers = { Authorization: `Bearer ${token}` };
      const branchParam = selectedBranch !== "all" ? selectedBranch : undefined;

      const [branchRes, topRes, avgRes, fastSlowRes] = await Promise.all([
        axios.get(`${API}/analytics/branch-overview`, { headers, params: { days } }),
        axios.get(`${API}/analytics/top-selling`, { headers, params: { branch: branchParam, days, limit: 15 } }),
        axios.get(`${API}/analytics/avg-daily-out`, { headers, params: { branch: branchParam, days } }),
        axios.get(`${API}/analytics/fast-slow-moving`, { headers, params: { branch: branchParam, days } })
      ]);

      setBranchOverview(branchRes.data);
      setTopSelling(topRes.data);
      setAvgDailyData(avgRes.data);
      setFastSlowData(fastSlowRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load analytics data");
    } finally {
      setLoading(false);
    }
  };

  // Fetch Sales by Date Range
  const fetchSalesByDateRange = async () => {
    setLoadingSalesByDate(true);
    try {
      const token = localStorage.getItem("auth_token");
      const response = await axios.get(`${API}/analytics/sales-by-date-range`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { 
          start_date: dateRangeStart, 
          end_date: dateRangeEnd,
          branch: reportBranch !== "all" ? reportBranch : undefined 
        }
      });
      setSalesByDate(response.data);
    } catch (error) {
      console.error("Error fetching sales by date:", error);
      toast.error("Failed to load sales data");
    } finally {
      setLoadingSalesByDate(false);
    }
  };

  // Fetch Days to Last
  const fetchDaysToLast = async () => {
    setLoadingDaysToLast(true);
    try {
      const token = localStorage.getItem("auth_token");
      const response = await axios.get(`${API}/analytics/days-to-last`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { 
          branch: daysToLastBranch !== "all" ? daysToLastBranch : undefined,
          days 
        }
      });
      
      // Transform API response: combine critical, low, out_of_stock, ok into items array
      const data = response.data;
      const allItems = [
        ...(data.critical || []).map(item => ({ ...item, priority: 'CRITICAL' })),
        ...(data.out_of_stock || []).map(item => ({ ...item, priority: 'OUT' })),
        ...(data.low || []).map(item => ({ ...item, priority: 'LOW' })),
        ...(data.ok || []).map(item => ({ ...item, priority: 'OK' }))
      ];
      
      setDaysToLastData({ ...data, items: allItems });
    } catch (error) {
      console.error("Error fetching days to last:", error);
    } finally {
      setLoadingDaysToLast(false);
    }
  };

  // Fetch Monthly Comparison
  const fetchMonthlyComparison = async () => {
    try {
      const token = localStorage.getItem("auth_token");
      const response = await axios.get(`${API}/analytics/monthly-comparison`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { month_a: monthA, month_b: monthB }
      });
      
      // Transform API response to match frontend expectations
      const data = response.data;
      const comparison = data.comparison || {};
      
      // Create branches array from comparison data
      const branches = [
        {
          branch: "Walk-In Sales",
          month_a_total: comparison.walk_in?.month_a?.qty || 0,
          month_b_total: comparison.walk_in?.month_b?.qty || 0
        },
        {
          branch: "Online Sales",
          month_a_total: comparison.online?.month_a?.qty || 0,
          month_b_total: comparison.online?.month_b?.qty || 0
        },
        {
          branch: "Pull Out",
          month_a_total: comparison.pull_out?.month_a?.qty || 0,
          month_b_total: comparison.pull_out?.month_b?.qty || 0
        }
      ];
      
      // Calculate totals
      const totals = {
        month_a: branches.reduce((sum, b) => sum + (b.month_a_total || 0), 0),
        month_b: branches.reduce((sum, b) => sum + (b.month_b_total || 0), 0)
      };
      
      setMonthlyComparison({ ...data, branches, totals });
    } catch (error) {
      console.error("Error fetching comparison:", error);
    }
  };

  // Fetch data summary for clear
  const fetchDataSummary = async (branch) => {
    try {
      const token = localStorage.getItem("auth_token");
      const { from, to } = getDateRange();
      const response = await axios.get(`${API}/admin/test-data-summary`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { branch: branch || clearBranch, date_from: from, date_to: to }
      });
      setDataSummary(response.data);
    } catch (error) {
      console.error("Error fetching summary:", error);
    }
  };

  // Fetch pending deletions
  const fetchPendingDeletions = async () => {
    try {
      const token = localStorage.getItem("auth_token");
      const response = await axios.get(`${API}/admin/pending-deletions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingDeletions(response.data.pending_deletions || []);
    } catch (error) {
      console.error("Error fetching pending deletions:", error);
    }
  };

  // Handle clear data - USE SOFT DELETE for undo support
  const handleClearData = async () => {
    setClearing(true);
    try {
      const token = localStorage.getItem("auth_token");
      const { from, to } = getDateRange();
      // USE SOFT DELETE ENDPOINT (with undo support)
      await axios.post(`${API}/admin/clear-selected-data`, {
        branch: clearBranch,
        walk_in_sales: clearOptions.walk_in_sales,
        online_sales: clearOptions.online_sales,
        warranty_transactions: clearOptions.warranty_transactions,
        inventory_records: clearOptions.inventory_records,
        movement_logs: clearOptions.movement_logs,
        date_from: from,
        date_to: to
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Data cleared for ${clearBranch}. You can UNDO within 15 minutes!`);
      setShowClearConfirm(false);
      fetchAllData();
      fetchPendingDeletions();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to clear data");
    } finally {
      setClearing(false);
    }
  };

  // Handle undo
  const handleUndo = async (deletionId) => {
    try {
      const token = localStorage.getItem("auth_token");
      const response = await axios.post(`${API}/admin/undo-delete/${deletionId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchPendingDeletions();
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Undo failed");
    }
  };

  // Quick delete ALL records for a branch (with date filter)
  const handleQuickDelete = async () => {
    if (!quickDeleteBranch) return;
    
    // SAFETY CHECK: Require typing "DELETE" to confirm
    if (deleteConfirmText !== "DELETE") {
      toast.error("I-type mo ang 'DELETE' para ma-confirm");
      return;
    }
    
    setQuickDeleting(true);
    try {
      const token = localStorage.getItem("auth_token");
      // USE SOFT DELETE ENDPOINT (with undo support) instead of hard delete
      const response = await axios.post(`${API}/admin/clear-selected-data`, {
        branch: quickDeleteBranch,
        walk_in_sales: true,
        online_sales: true,
        warranty_transactions: true,
        inventory_records: true,
        movement_logs: true,
        date_from: quickDateFrom,
        date_to: quickDateTo
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Show success with undo info
      const deletedCount = response.data.deleted_counts || {};
      const total = Object.values(deletedCount).reduce((a, b) => a + b, 0);
      toast.success(
        `Deleted ${total} records for ${quickDeleteBranch}. You can UNDO within 15 minutes!`,
        { duration: 10000 }
      );
      
      setShowQuickDelete(false);
      setQuickDeleteBranch(null);
      setDeleteConfirmText(""); // Reset confirmation text
      fetchAllData();
      fetchPendingDeletions(); // Refresh pending deletions immediately
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete");
    } finally {
      setQuickDeleting(false);
    }
  };

  useEffect(() => {
    fetchAllData();
    if (isAdmin) fetchPendingDeletions();
  }, [selectedBranch, days]);

  // Auto-refresh pending deletions every 30 seconds (for undo timer)
  useEffect(() => {
    if (!isAdmin) return;
    const interval = setInterval(() => {
      fetchPendingDeletions();
    }, 30000);
    return () => clearInterval(interval);
  }, [isAdmin]);

  useEffect(() => {
    fetchSalesByDateRange();
  }, [dateRangeStart, dateRangeEnd, reportBranch]);

  useEffect(() => {
    fetchDaysToLast();
  }, [daysToLastBranch, days]);

  useEffect(() => {
    fetchMonthlyComparison();
  }, [monthA, monthB]);

  // Priority color helper
  const getPriorityClasses = (priority) => {
    switch (priority) {
      case 'CRITICAL': return 'bg-red-600 text-white';
      case 'HIGH': return 'bg-red-500 text-white';
      case 'MEDIUM': return 'bg-amber-500 text-white';
      case 'WATCH': return 'bg-yellow-500 text-black';
      case 'LOW': return 'bg-slate-400 text-white';
      default: return 'bg-slate-300 text-slate-700';
    }
  };

  // Change indicator
  const ChangeIndicator = ({ current, previous }) => {
    if (previous === 0 && current === 0) return <Minus className="h-4 w-4 text-slate-400" />;
    const change = previous > 0 ? ((current - previous) / previous * 100).toFixed(0) : (current > 0 ? 100 : 0);
    const isPositive = current >= previous;
    return (
      <span className={`flex items-center gap-1 text-sm font-medium ${isPositive ? 'text-emerald-600' : 'text-red-600'}`}>
        {isPositive ? <ArrowUpRight className="h-4 w-4" /> : <ArrowDownRight className="h-4 w-4" />}
        {Math.abs(change)}%
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="analytics-dashboard">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-semibold text-slate-900">Analytics Dashboard</h1>
              <p className="text-sm text-slate-500">Inventory & Sales Overview</p>
            </div>
            <div className="flex items-center gap-3">
              <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
                <SelectTrigger className="w-[110px] h-9 bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">7 days</SelectItem>
                  <SelectItem value="14">14 days</SelectItem>
                  <SelectItem value="30">30 days</SelectItem>
                  <SelectItem value="60">60 days</SelectItem>
                  <SelectItem value="90">90 days</SelectItem>
                </SelectContent>
              </Select>
              <Select value={selectedBranch} onValueChange={setSelectedBranch}>
                <SelectTrigger className="w-[150px] h-9 bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(BRANCHES).map(([code, name]) => (
                    <SelectItem key={code} value={code}>{name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="ghost" size="icon" onClick={fetchAllData} disabled={loading} className="h-9 w-9">
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
              {isAdmin && (
                <Button 
                  variant="outline"
                  size="sm"
                  onClick={() => { setShowClearConfirm(true); fetchDataSummary(); }}
                  className="h-9 text-red-600 border-red-200 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Clear Data
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Pending Undo Banner - Show when there are deletions that can be undone */}
        {pendingDeletions.length > 0 && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-300 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Undo2 className="h-5 w-5 text-amber-600" />
              <span className="font-semibold text-amber-800">Pending Deletions - You can UNDO!</span>
            </div>
            <div className="space-y-2">
              {pendingDeletions.map(del => {
                const expiresAt = new Date(del.undo_expires_at);
                const now = new Date();
                const minutesLeft = Math.max(0, Math.round((expiresAt - now) / 60000));
                return (
                  <div key={del.deletion_id} className="flex items-center justify-between bg-white p-3 rounded border">
                    <div>
                      <span className="font-medium text-slate-900">{del.branch}</span>
                      <span className="text-slate-500 text-sm ml-2">
                        {del.total_count} records ({del.date_from} to {del.date_to})
                      </span>
                      <span className="text-amber-600 text-sm ml-2">
                        ⏰ {minutesLeft} min left to undo
                      </span>
                    </div>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => handleUndo(del.deletion_id)}
                      className="border-amber-400 text-amber-700 hover:bg-amber-100"
                    >
                      <Undo2 className="h-4 w-4 mr-1" />
                      UNDO
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
          </div>
        ) : (
          <Tabs defaultValue="overview" className="space-y-6">
            <TabsList className="bg-white border p-1 h-auto">
              <TabsTrigger value="overview" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white px-4 py-2">
                Overview
              </TabsTrigger>
              <TabsTrigger value="daily-sales" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white px-4 py-2">
                Daily Sales
              </TabsTrigger>
              <TabsTrigger value="fast-slow" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white px-4 py-2">
                Fast/Slow Moving
              </TabsTrigger>
              <TabsTrigger value="days-to-last" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white px-4 py-2">
                Days to Last
              </TabsTrigger>
              <TabsTrigger value="comparison" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white px-4 py-2">
                Monthly Comparison
              </TabsTrigger>
            </TabsList>

            {/* OVERVIEW TAB */}
            <TabsContent value="overview" className="space-y-6">
              {/* Branch Cards */}
              <div className="grid grid-cols-5 gap-4">
                {branchOverview?.branches?.map(b => (
                  <Card key={b.branch} className="bg-white">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <span className="font-semibold text-lg text-slate-900">{b.branch}</span>
                        <span className="text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded">{b.item_count} items</span>
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-slate-500">Stock</span>
                          <span className="font-semibold text-slate-900">{b.current_stock?.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-500">Walk-In</span>
                          <span className="font-semibold text-emerald-600">{b.walk_in_sales}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-500">Online</span>
                          <span className="font-semibold text-blue-600">{b.online_sales}</span>
                        </div>
                        <div className="flex justify-between pt-2 border-t">
                          <span className="font-medium text-slate-700">Total Out</span>
                          <span className="font-bold text-slate-900">{b.total_out}</span>
                        </div>
                      </div>
                      {/* Delete All Button */}
                      {isAdmin && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => { setQuickDeleteBranch(b.branch); setShowQuickDelete(true); }}
                          className="w-full mt-3 h-8 text-xs text-red-600 hover:text-red-700 hover:bg-red-50 border border-red-200"
                        >
                          <Trash2 className="h-3 w-3 mr-1" />
                          Delete All Records
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Top Selling & Avg Daily */}
              <div className="grid grid-cols-2 gap-6">
                <Card className="bg-white">
                  <CardHeader className="pb-3 border-b">
                    <CardTitle className="text-base font-semibold flex items-center gap-2">
                      <TrendingUp className="h-4 w-4 text-emerald-600" />
                      Top Selling Items
                      <span className="ml-auto text-xs font-normal text-slate-500">{days} days</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="divide-y max-h-80 overflow-y-auto">
                      {topSelling?.items?.length > 0 ? topSelling.items.map((item, idx) => (
                        <div key={idx} className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50">
                          <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                            idx === 0 ? 'bg-amber-400 text-amber-900' :
                            idx === 1 ? 'bg-slate-300 text-slate-700' :
                            idx === 2 ? 'bg-orange-300 text-orange-800' :
                            'bg-slate-100 text-slate-600'
                          }`}>
                            {idx + 1}
                          </span>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm text-slate-900 truncate">{item.item_name}</p>
                            <p className="text-xs text-slate-500">{item.transactions} transactions</p>
                          </div>
                          <span className="font-semibold text-emerald-600">{item.total_qty}</span>
                        </div>
                      )) : (
                        <div className="py-8 text-center text-slate-400">No data available</div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-white">
                  <CardHeader className="pb-3 border-b">
                    <CardTitle className="text-base font-semibold flex items-center gap-2">
                      <Clock className="h-4 w-4 text-blue-600" />
                      Average Daily Out
                      <span className="ml-auto text-xs font-normal text-slate-500">{days} days</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="divide-y max-h-80 overflow-y-auto">
                      {avgDailyData?.items?.length > 0 ? avgDailyData.items.slice(0, 15).map((item, idx) => (
                        <div key={idx} className="flex items-center justify-between px-4 py-3 hover:bg-slate-50">
                          <span className="font-medium text-sm text-slate-900 truncate flex-1">{item.item_name}</span>
                          <div className="text-right">
                            <div className="font-semibold text-blue-600">{item.avg_daily}/day</div>
                            <div className="text-xs text-slate-500">Total: {item.total_out}</div>
                          </div>
                        </div>
                      )) : (
                        <div className="py-8 text-center text-slate-400">No data available</div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* DAILY SALES TAB */}
            <TabsContent value="daily-sales" className="space-y-6">
              <Card className="bg-white">
                <CardHeader className="border-b">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base font-semibold flex items-center gap-2">
                      <CalendarDays className="h-4 w-4 text-violet-600" />
                      Sales Per Day
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-2 bg-slate-100 rounded-lg p-1">
                        {[7, 14, 30].map(d => (
                          <Button
                            key={d}
                            variant="ghost"
                            size="sm"
                            className={`h-7 px-3 text-xs ${
                              Math.round((new Date(dateRangeEnd) - new Date(dateRangeStart)) / (1000 * 60 * 60 * 24)) === d
                                ? 'bg-white shadow-sm'
                                : ''
                            }`}
                            onClick={() => {
                              const end = new Date();
                              const start = new Date();
                              start.setDate(start.getDate() - d);
                              setDateRangeStart(start.toISOString().slice(0, 10));
                              setDateRangeEnd(end.toISOString().slice(0, 10));
                            }}
                          >
                            {d}D
                          </Button>
                        ))}
                      </div>
                      <Input
                        type="date"
                        value={dateRangeStart}
                        onChange={(e) => setDateRangeStart(e.target.value)}
                        className="w-36 h-8 text-sm"
                      />
                      <span className="text-slate-400">→</span>
                      <Input
                        type="date"
                        value={dateRangeEnd}
                        onChange={(e) => setDateRangeEnd(e.target.value)}
                        className="w-36 h-8 text-sm"
                      />
                      <Select value={reportBranch} onValueChange={setReportBranch}>
                        <SelectTrigger className="w-[140px] h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(BRANCHES).map(([code, name]) => (
                            <SelectItem key={code} value={code}>{name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  {loadingSalesByDate ? (
                    <div className="py-12 text-center">
                      <RefreshCw className="h-6 w-6 animate-spin text-slate-400 mx-auto" />
                    </div>
                  ) : salesByDate?.daily_sales?.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50 border-b">
                          <tr>
                            <th className="text-left px-4 py-3 font-medium text-slate-600">Date</th>
                            <th className="text-right px-4 py-3 font-medium text-slate-600">Walk-In</th>
                            <th className="text-right px-4 py-3 font-medium text-slate-600">Online</th>
                            <th className="text-right px-4 py-3 font-medium text-slate-600">Total</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {salesByDate.daily_sales.map((day, idx) => (
                            <tr key={idx} className="hover:bg-slate-50">
                              <td className="px-4 py-3 font-medium text-slate-900">
                                {new Date(day.date + 'T00:00:00').toLocaleDateString('en-PH', { weekday: 'short', month: 'short', day: 'numeric' })}
                              </td>
                              <td className="px-4 py-3 text-right text-emerald-600 font-medium">{day.walk_in || 0}</td>
                              <td className="px-4 py-3 text-right text-blue-600 font-medium">{day.online || 0}</td>
                              <td className="px-4 py-3 text-right font-semibold text-slate-900">{day.total || 0}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="bg-slate-100 border-t-2">
                          <tr>
                            <td className="px-4 py-3 font-semibold text-slate-900">TOTAL</td>
                            <td className="px-4 py-3 text-right font-semibold text-emerald-600">{salesByDate.totals?.walk_in || 0}</td>
                            <td className="px-4 py-3 text-right font-semibold text-blue-600">{salesByDate.totals?.online || 0}</td>
                            <td className="px-4 py-3 text-right font-bold text-slate-900">{salesByDate.totals?.total || 0}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  ) : (
                    <div className="py-12 text-center text-slate-400">No sales data for this period</div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* FAST/SLOW MOVING TAB */}
            <TabsContent value="fast-slow" className="space-y-6">
              <div className="grid grid-cols-3 gap-6">
                {/* Fast Moving */}
                <Card className="bg-white">
                  <CardHeader className="border-b bg-emerald-50">
                    <CardTitle className="text-base font-semibold flex items-center gap-2 text-emerald-800">
                      <Zap className="h-4 w-4" />
                      Fast Moving Items
                      <span className="ml-auto text-xs font-normal text-emerald-600">{fastSlowData?.fast_moving?.length || 0} items</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="divide-y max-h-96 overflow-y-auto">
                      {fastSlowData?.fast_moving?.length > 0 ? fastSlowData.fast_moving.map((item, idx) => (
                        <div key={idx} className="flex items-center justify-between px-4 py-3 hover:bg-slate-50">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm text-slate-900 truncate">{item.item_name}</p>
                            <p className="text-xs text-slate-500">Stock: {item.current_stock}</p>
                          </div>
                          <div className="text-right">
                            <div className="font-semibold text-emerald-600">{item.avg_daily}/day</div>
                          </div>
                        </div>
                      )) : (
                        <div className="py-8 text-center text-slate-400">No fast-moving items</div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Slow Moving */}
                <Card className="bg-white">
                  <CardHeader className="border-b bg-amber-50">
                    <CardTitle className="text-base font-semibold flex items-center gap-2 text-amber-800">
                      <Clock className="h-4 w-4" />
                      Slow Moving Items
                      <span className="ml-auto text-xs font-normal text-amber-600">{fastSlowData?.slow_moving?.length || 0} items</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="divide-y max-h-96 overflow-y-auto">
                      {fastSlowData?.slow_moving?.length > 0 ? fastSlowData.slow_moving.map((item, idx) => (
                        <div key={idx} className="flex items-center justify-between px-4 py-3 hover:bg-slate-50">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm text-slate-900 truncate">{item.item_name}</p>
                            <p className="text-xs text-slate-500">Stock: {item.current_stock}</p>
                          </div>
                          <div className="text-right">
                            <div className="font-semibold text-amber-600">{item.avg_daily}/day</div>
                          </div>
                        </div>
                      )) : (
                        <div className="py-8 text-center text-slate-400">No slow-moving items</div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Non-Moving Items */}
                <Card className="bg-white">
                  <CardHeader className="border-b bg-slate-100">
                    <CardTitle className="text-base font-semibold flex items-center gap-2 text-slate-700">
                      <XCircle className="h-4 w-4" />
                      Non-Moving Items
                      <span className="ml-auto text-xs font-normal text-slate-500">{fastSlowData?.non_moving?.length || 0} items</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="divide-y max-h-96 overflow-y-auto">
                      {fastSlowData?.non_moving?.length > 0 ? fastSlowData.non_moving.map((item, idx) => (
                        <div key={idx} className="flex items-center justify-between px-4 py-3 hover:bg-slate-50">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm text-slate-900 truncate">{item.item_name}</p>
                            <p className="text-xs text-slate-500">Stock: {item.current_stock}</p>
                          </div>
                          <div className="text-right">
                            <div className="font-semibold text-slate-500">0/day</div>
                          </div>
                        </div>
                      )) : (
                        <div className="py-8 text-center text-slate-400">No non-moving items</div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* DAYS TO LAST TAB */}
            <TabsContent value="days-to-last" className="space-y-6">
              <Card className="bg-white">
                <CardHeader className="border-b">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base font-semibold flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-amber-600" />
                      Stock Projection - Days Until Out
                    </CardTitle>
                    <Select value={daysToLastBranch} onValueChange={setDaysToLastBranch}>
                      <SelectTrigger className="w-[150px] h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(BRANCHES).map(([code, name]) => (
                          <SelectItem key={code} value={code}>{name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  {loadingDaysToLast ? (
                    <div className="py-12 text-center">
                      <RefreshCw className="h-6 w-6 animate-spin text-slate-400 mx-auto" />
                    </div>
                  ) : daysToLastData?.items?.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50 border-b">
                          <tr>
                            <th className="text-left px-4 py-3 font-medium text-slate-600">Item</th>
                            <th className="text-center px-4 py-3 font-medium text-slate-600">Priority</th>
                            <th className="text-right px-4 py-3 font-medium text-slate-600">Stock</th>
                            <th className="text-right px-4 py-3 font-medium text-slate-600">Avg/Day</th>
                            <th className="text-right px-4 py-3 font-medium text-slate-600">Days Left</th>
                            <th className="text-right px-4 py-3 font-medium text-slate-600">Proj. Out</th>
                            <th className="text-right px-4 py-3 font-medium text-slate-600">Suggested</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {daysToLastData.items.map((item, idx) => {
                            const calculated = calculateSmartRestockData(item);
                            return (
                              <tr key={idx} className="hover:bg-slate-50">
                                <td className="px-4 py-3 font-medium text-slate-900">{item.item_name}</td>
                                <td className="px-4 py-3 text-center">
                                  <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${getPriorityClasses(calculated.priorityLabel)}`}>
                                    {calculated.priorityLabel}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-right font-medium">{item.current_stock}</td>
                                <td className="px-4 py-3 text-right">{item.avg_daily}</td>
                                <td className={`px-4 py-3 text-right font-semibold ${
                                  item.current_stock === 0 ? 'text-red-600' :
                                  typeof item.days_left === 'number' && item.days_left <= 7 ? 'text-red-600' :
                                  typeof item.days_left === 'number' && item.days_left <= 14 ? 'text-amber-600' :
                                  'text-slate-900'
                                }`}>
                                  {item.current_stock === 0 ? 'OUT' : item.days_left}
                                </td>
                                <td className="px-4 py-3 text-right text-slate-600">{calculated.projectedOutDate || '-'}</td>
                                <td className="px-4 py-3 text-right font-semibold text-blue-600">{calculated.suggestedRestock > 0 ? `+${calculated.suggestedRestock}` : '-'}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="py-12 text-center text-slate-400">No data available</div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* MONTHLY COMPARISON TAB */}
            <TabsContent value="comparison" className="space-y-6">
              <Card className="bg-white">
                <CardHeader className="border-b">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base font-semibold flex items-center gap-2">
                      <BarChart3 className="h-4 w-4 text-violet-600" />
                      Monthly Branch Comparison
                    </CardTitle>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-slate-500">Month A:</span>
                        <Input
                          type="month"
                          value={monthA}
                          onChange={(e) => setMonthA(e.target.value)}
                          className="w-40 h-8"
                        />
                      </div>
                      <span className="text-slate-400">vs</span>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-slate-500">Month B:</span>
                        <Input
                          type="month"
                          value={monthB}
                          onChange={(e) => setMonthB(e.target.value)}
                          className="w-40 h-8"
                        />
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  {monthlyComparison?.branches?.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50 border-b">
                          <tr>
                            <th className="text-left px-4 py-3 font-medium text-slate-600">Branch</th>
                            <th className="text-right px-4 py-3 font-medium text-slate-600">
                              {new Date(monthA + '-01').toLocaleDateString('en-PH', { month: 'short', year: 'numeric' })}
                            </th>
                            <th className="text-right px-4 py-3 font-medium text-slate-600">
                              {new Date(monthB + '-01').toLocaleDateString('en-PH', { month: 'short', year: 'numeric' })}
                            </th>
                            <th className="text-center px-4 py-3 font-medium text-slate-600">Change</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {monthlyComparison.branches.map((branch, idx) => (
                            <tr key={idx} className="hover:bg-slate-50">
                              <td className="px-4 py-3 font-semibold text-slate-900">{branch.branch}</td>
                              <td className="px-4 py-3 text-right">{branch.month_a_total || 0}</td>
                              <td className="px-4 py-3 text-right">{branch.month_b_total || 0}</td>
                              <td className="px-4 py-3 text-center">
                                <ChangeIndicator current={branch.month_b_total || 0} previous={branch.month_a_total || 0} />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="bg-slate-100 border-t-2">
                          <tr>
                            <td className="px-4 py-3 font-semibold text-slate-900">TOTAL</td>
                            <td className="px-4 py-3 text-right font-semibold">{monthlyComparison.totals?.month_a || 0}</td>
                            <td className="px-4 py-3 text-right font-semibold">{monthlyComparison.totals?.month_b || 0}</td>
                            <td className="px-4 py-3 text-center">
                              <ChangeIndicator current={monthlyComparison.totals?.month_b || 0} previous={monthlyComparison.totals?.month_a || 0} />
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  ) : (
                    <div className="py-12 text-center text-slate-400">No comparison data available</div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        )}
      </div>

      {/* Clear Data Dialog */}
      <Dialog open={showClearConfirm} onOpenChange={setShowClearConfirm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              Clear Test Data
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">Branch</label>
              <Select value={clearBranch} onValueChange={(v) => { setClearBranch(v); fetchDataSummary(v); }}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(BRANCH_ONLY).map(([code, name]) => (
                    <SelectItem key={code} value={code}>{code} - {name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">Date Range</label>
              <div className="flex items-center gap-2">
                <Input
                  type="date"
                  value={customDateFrom}
                  onChange={(e) => { setCustomDateFrom(e.target.value); setDateRangeType("custom"); }}
                  className="flex-1"
                />
                <span className="text-slate-400">to</span>
                <Input
                  type="date"
                  value={customDateTo}
                  onChange={(e) => { setCustomDateTo(e.target.value); setDateRangeType("custom"); }}
                  className="flex-1"
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">Data to Clear</label>
              <div className="space-y-2">
                {[
                  { key: 'walk_in_sales', label: 'Walk-In Sales', count: dataSummary?.data_to_clear?.walk_in_sales },
                  { key: 'online_sales', label: 'Online Sales', count: dataSummary?.data_to_clear?.online_sales },
                  { key: 'inventory_records', label: 'Inventory Records', count: dataSummary?.data_to_clear?.inventory_records },
                  { key: 'movement_logs', label: 'Movement Logs', count: dataSummary?.data_to_clear?.movement_logs },
                ].map(item => (
                  <div key={item.key} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={clearOptions[item.key]}
                        onCheckedChange={(checked) => setClearOptions(prev => ({ ...prev, [item.key]: checked }))}
                      />
                      <span className="text-sm">{item.label}</span>
                    </div>
                    <span className="text-sm font-medium text-slate-600">{item.count || 0}</span>
                  </div>
                ))}
              </div>
            </div>

            {pendingDeletions.length > 0 && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded">
                <p className="text-sm font-medium text-amber-800 mb-2">Pending Undo</p>
                {pendingDeletions.map(del => (
                  <div key={del.id} className="flex items-center justify-between">
                    <span className="text-xs text-amber-700">{del.branch} - {del.total_records} records</span>
                    <Button size="sm" variant="ghost" onClick={() => handleUndo(del.id)} className="h-6 text-xs">
                      <Undo2 className="h-3 w-3 mr-1" /> Undo
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowClearConfirm(false)}>Cancel</Button>
            <Button 
              variant="destructive" 
              onClick={handleClearData}
              disabled={clearing}
            >
              {clearing ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Clear Selected
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quick Delete Confirmation Dialog - ADMIN ONLY with SAFETY CONFIRMATION */}
      <Dialog open={showQuickDelete} onOpenChange={(open) => { 
        setShowQuickDelete(open); 
        if (!open) setDeleteConfirmText(""); // Reset on close
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              ⚠️ DANGER: Delete Records - {quickDeleteBranch}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* CRITICAL WARNING */}
            <div className="bg-red-100 border-2 border-red-500 rounded-lg p-4">
              <p className="font-bold text-red-700 text-center text-lg mb-2">⚠️ BABALA ⚠️</p>
              <p className="text-red-700 text-sm text-center">
                Ang lahat ng records sa <strong>{quickDeleteBranch}</strong> branch ay PERMANENTENG MATATANGGAL!
              </p>
              <p className="text-red-600 text-xs text-center mt-2">
                May 15-minute UNDO window pero pagkatapos noon, WALA NANG RECOVERY!
              </p>
            </div>

            {/* Date Range Filter */}
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">Date Range na ide-delete</label>
              <div className="flex items-center gap-2">
                <Input
                  type="date"
                  value={quickDateFrom}
                  onChange={(e) => setQuickDateFrom(e.target.value)}
                  className="flex-1"
                />
                <span className="text-slate-400">to</span>
                <Input
                  type="date"
                  value={quickDateTo}
                  onChange={(e) => setQuickDateTo(e.target.value)}
                  className="flex-1"
                />
              </div>
              {/* Quick Presets */}
              <div className="flex gap-2 mt-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="text-xs h-7"
                  onClick={() => {
                    setQuickDateFrom("2020-01-01");
                    setQuickDateTo("2026-02-28");
                  }}
                >
                  Before March 2026
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-xs h-7"
                  onClick={() => {
                    setQuickDateFrom("2020-01-01");
                    setQuickDateTo(new Date().toISOString().slice(0, 10));
                  }}
                >
                  All Time
                </Button>
              </div>
            </div>

            <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700">
              <p className="font-medium mb-1">Matatanggal na data ({quickDateFrom} to {quickDateTo}):</p>
              <ul className="list-disc list-inside space-y-1 text-xs">
                <li>Walk-In Sales records</li>
                <li>Online Sales records</li>
                <li>Inventory Records (LAHAT ng stocks!)</li>
                <li>Movement Logs</li>
              </ul>
            </div>

            {/* CONFIRMATION TEXT INPUT */}
            <div className="bg-amber-50 border-2 border-amber-400 rounded-lg p-4">
              <label className="text-sm font-bold text-amber-800 block mb-2">
                Para ma-confirm, i-type ang: <span className="text-red-600 font-mono">DELETE</span>
              </label>
              <Input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value.toUpperCase())}
                placeholder="Type DELETE here..."
                className={`font-mono text-center text-lg ${
                  deleteConfirmText === "DELETE" 
                    ? "border-green-500 bg-green-50" 
                    : "border-amber-400"
                }`}
                data-testid="delete-confirm-input"
              />
              {deleteConfirmText && deleteConfirmText !== "DELETE" && (
                <p className="text-xs text-red-500 mt-1 text-center">Hindi tama. I-type ang "DELETE" exactly.</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowQuickDelete(false); setDeleteConfirmText(""); }}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleQuickDelete}
              disabled={quickDeleting || deleteConfirmText !== "DELETE"}
              className={deleteConfirmText !== "DELETE" ? "opacity-50 cursor-not-allowed" : ""}
            >
              {quickDeleting ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
              {deleteConfirmText === "DELETE" ? "CONFIRM DELETE" : "Type DELETE to enable"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
